import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';

function LoginShell({ children }) {
  return (
    <div className="login-shell">
      <div className="login-card">{children}</div>
    </div>
  );
}

export default function Login({ portal = 'app' }) {
  const isAdminPortal = portal === 'admin';
  const {
    login,
    isAuthenticated,
    isAdmin,
    canManageAssignments,
    admin,
    elevatedAdmin,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const elevate = Boolean(location.state?.elevate);
  const redirectTo = location.state?.from || (isAdminPortal ? '/manage' : '/');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (elevate && canManageAssignments) {
    return (
      <LoginShell>
        <div className="card">
          <div className="card-title">Admin access already unlocked</div>
          <p className="text-muted">
            {elevatedAdmin?.email || admin?.email} can edit assignments. Zaman stays signed in.
          </p>
          <button className="btn btn-primary w100" onClick={() => navigate(redirectTo, { replace: true })}>
            Back to {redirectTo === '/' ? 'dashboard' : 'page'}
          </button>
        </div>
      </LoginShell>
    );
  }

  if (isAuthenticated && !elevate) {
    const home = isAdmin ? '/manage' : '/';
    const wrongPortal = (isAdminPortal && !isAdmin) || (!isAdminPortal && isAdmin);
    return (
      <LoginShell>
        <div className="card">
          <div className="card-title">You are already signed in</div>
          <p className="text-muted">
            Signed in as {admin?.email} ({admin?.role || 'user'}).
          </p>
          {wrongPortal && (
            <p className="text-sm warn-text mb12">
              {isAdmin
                ? 'Admin accounts must use the Admin Portal login.'
                : 'Staff accounts must use the Staff App login.'}
            </p>
          )}
          <button className="btn btn-primary w100" onClick={() => navigate(home)}>
            Continue
          </button>
        </div>
      </LoginShell>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Enter email and password');
      return;
    }
    setSubmitting(true);
    try {
      const user = await login(email.trim(), password, portal, { elevate });
      if (elevate) {
        toast.success('Admin access unlocked — Zaman stays signed in');
        navigate(redirectTo, { replace: true });
        return;
      }
      const label = user?.role === 'admin'
        ? 'admin'
        : (user?.role === 'dispatcher' ? 'dispatcher' : 'staff');
      toast.success(`Signed in as ${label}`);
      const target = user?.role === 'admin'
        ? '/manage'
        : (user?.role === 'dispatcher' ? '/assignments' : redirectTo);
      navigate(target, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Login failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginShell>
      <div className="card">
        <div className="card-title login-title">
          {elevate
            ? 'Unlock admin access'
            : (isAdminPortal ? 'Admin Portal' : 'Staff App Login')}
        </div>
        <p className="text-muted text-sm mb16">
          {elevate
            ? 'Sign in as admin or dispatcher to edit vehicle/wash assignments. Zaman stays signed in for daily work.'
            : (isAdminPortal
              ? 'Admin settings, vehicles, drivers, SMTP, and notifications.'
              : 'For Zaman and guards — log in once; session stays active. Trips, wash, fuel, day-end, and reports.')}
        </p>
        <form onSubmit={onSubmit} className="form-grid">
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={elevate || isAdminPortal ? 'admin@company.com' : 'staff@company.com'}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button className="btn btn-primary w100" type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : (elevate ? 'Unlock' : 'Sign in')}
          </button>
        </form>
        {!elevate && (
          <p className="text-muted text-sm mt16">
            {isAdminPortal ? (
              <>Need the operations app? <Link to="/login">Staff app login</Link></>
            ) : (
              <>Need admin settings? <Link to="/admin/login">Admin portal login</Link></>
            )}
          </p>
        )}
        {elevate && isAuthenticated && (
          <p className="text-muted text-sm mt16">
            Staff session active as <strong>{admin?.email}</strong>.
          </p>
        )}
      </div>
    </LoginShell>
  );
}
